import { Formik, type FormikProps } from 'formik'
import React, { useEffect, useRef } from 'react'

import { Dashboard } from '@components/common/Dashboard/Dashboard'
import { PageLayout } from '@components/common/PageLayout'
import { DashboardBackButton } from '@components/utils'
import {
  Banner,
  BaseContent,
  ButtonClean,
  HeadContent,
  Subsection,
  Tag,
  useModalContext,
} from '@fourthwall/components'
import { routing } from '@utils/routing'

import { mapStatusToTagProps } from '../../utils/mapStatusToTagProps'
import type { SettingsFormValues } from './AppsShipStation.types'
import { ConfirmStoreDisconnectModal } from './components/ConfirmStoreDisconnectModal/ConfirmStoreDisconnectModal'
import { SettingsForm } from './components/SettingsForm/SettingsForm'
import { useShipStation } from './hooks/useShipStation'

export const AppsShipStation = () => {
  const { shipStation } = useShipStation()
  const { open } = useModalContext()
  const formRef = useRef<FormikProps<SettingsFormValues>>(null)

  useEffect(() => {
    if (!shipStation.settings.stores.length) {
      formRef.current?.setFieldValue('storeId', '')

      return
    }

    const storeId = shipStation.settings.initialValues.storeId
    const updatedStoreId =
      storeId && shipStation.settings.stores?.some((store) => store.value === storeId)
        ? storeId.toString()
        : ''

    formRef.current?.setFieldValue('storeId', updatedStoreId)
  }, [shipStation.settings.stores])

  const handleDisconnect = () => {
    open(ConfirmStoreDisconnectModal, {
      type: 'DISCONNECT',
      pendingFulfillmentsCount: shipStation.pendingFulfillmentsCount,
      onConfirm: () => {
        formRef.current?.resetForm({
          values: {
            apiSecret: '',
            apiKey: '',
            storeId: '',
          },
        })
        shipStation.onDisconnect()
      },
    })
  }

  const renderStatusTag = () => {
    const props = mapStatusToTagProps(shipStation.status)

    if (!props) return null

    return (
      <div style={{ marginBottom: 20 }}>
        <Tag {...props} size="large" display="inline-flex" />
      </div>
    )
  }

  const getSettingsErrorBanner = () => {
    if (shipStation.settings.storesErrorCode === 'SHIPSTATION_INCORRECT_CREDENTIALS_ERROR') {
      return (
        <Banner appearance="danger">
          Please verify your ShipStation credentials and try again.
        </Banner>
      )
    }

    if (shipStation.settings.storesErrorCode === 'SHIPSTATION_PAYMENT_REQUIRED_ERROR') {
      return (
        <Banner appearance="danger">
          Please verify your{' '}
          <a href={routing.external.shipStation.paymentSettings} target="_blank" rel="noreferrer">
            ShipStation license payment
          </a>{' '}
          and try again.
        </Banner>
      )
    }

    if (shipStation.settings.storesErrorCode === 'SHIPSTATION_INCORRECT_CONFIGURATION_ERROR') {
      return (
        <Banner appearance="danger">
          Please verify your ShipStation configuration and try again.
        </Banner>
      )
    }

    if (shipStation.settings.storesErrorCode === 'SHIPSTATION_INCORRECT_SETTINGS_ERROR') {
      return (
        <Banner appearance="danger">Please verify your ShipStation settings and try again.</Banner>
      )
    }
  }

  const renderSettingsForm = () => {
    if (shipStation.isLoading) {
      return null
    }
    const errorBanner = getSettingsErrorBanner()

    return (
      <Subsection title="Connection">
        <Formik<SettingsFormValues>
          innerRef={formRef}
          initialValues={shipStation.settings.initialValues}
          onSubmit={shipStation.settings.onSubmit}
        >
          <>
            <div style={{ marginBottom: '16px' }}>
              Connect your ShipStation account using your API key and secret from your{' '}
              <ButtonClean
                label="ShipStation API keys page"
                target="_blank"
                href={routing.external.shipStation.apiSettings}
              />{' '}
              in your ShipStation account
            </div>

            {errorBanner && <div style={{ marginBottom: '16px' }}>{errorBanner}</div>}

            <SettingsForm
              storesOptions={shipStation.settings.stores}
              storesWarning={
                shipStation.status === 'NOT_CONFIGURED'
                  ? 'Please select your store to finish configuration'
                  : undefined
              }
              isLoading={shipStation.settings.isLoading}
              isSubmitting={shipStation.settings.isSubmitting}
              onFetchStores={shipStation.settings.onFetchStores}
            />

            <div style={{ marginTop: 16 }}>
              <BaseContent>
                <ul>
                  <li>
                    Orders cancelled on ShipStation are automatically cancelled on Fourthwall as
                    well.
                  </li>
                  <li>
                    Orders edited on ShipStation (quantity change, product removal, etc.) will NOT
                    automatically be edited on Fourthwall
                  </li>
                  <li>
                    It takes up to 24 hours for new orders to be synced. Cancellations are synced
                    instantly.
                  </li>
                  <li>
                    You can disconnect at any time - your orders and data will remain safe
                    {(shipStation.status === 'CONNECTED' || shipStation.status === 'BROKEN') && (
                      <>
                        : <ButtonClean label="Disconnect" onClick={handleDisconnect} />
                      </>
                    )}
                  </li>
                </ul>
              </BaseContent>
            </div>
          </>
        </Formik>
      </Subsection>
    )
  }

  return (
    <>
      <HeadContent title="ShipStation" />
      <PageLayout size="tiny">
        <Dashboard.Section
          name="ShipStation"
          topContent={<DashboardBackButton label="Apps" to={routing.apps.self} />}
        >
          <Subsection>
            {renderStatusTag()}
            <div>
              Use ShipStation to manage the shipping of products you’re responsible for fulfilling.{' '}
              <ButtonClean
                label="Learn more"
                href={routing.external.helpCenter.shipStationIntegration}
                target="_blank"
              />
            </div>
          </Subsection>
          {renderSettingsForm()}
        </Dashboard.Section>
      </PageLayout>
    </>
  )
}
